import React from "react";
import { twMerge } from "tailwind-merge";
import { ControlBase } from "./ControlBase";
import type { ControlPosition } from "./utils";

export interface ControlGroupProps {
  position?: ControlPosition;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  /**
   * Orientation of the control group
   * - "vertical": Buttons stack top-to-bottom (default)
   * - "horizontal": Buttons arrange left-to-right
   * @default "vertical"
   */
  orientation?: "vertical" | "horizontal";
  /**
   * Spacing between controls in the group
   * - "sm": 8px gap
   * - "md": 16px gap
   * - "lg": 24px gap
   * @default "md"
   */
  spacing?: "sm" | "md" | "lg";
}

const SPACING_CLASSES = {
  sm: "gap-2", // 8px
  md: "gap-4", // 16px
  lg: "gap-6", // 24px
};

/**
 * Groups multiple control buttons together with consistent spacing
 *
 * Use this component when you need to:
 * 1. Stack multiple buttons within a single control (e.g., compass + geolocate)
 * 2. Stack multiple separate controls in the same corner (e.g., navigation + reset zoom)
 *
 * The group automatically handles:
 * - Consistent spacing between controls
 * - Vertical or horizontal orientation
 * - Proper positioning in map corners
 *
 * @example
 * // Single control with multiple buttons stacked
 * <ControlGroup position="bottom-right">
 *   <ControlButton icon="Compass" onPress={handleCompass} />
 *   <ControlButton icon="NavigationArrow" onPress={handleGeolocate} />
 * </ControlGroup>
 *
 * @example
 * // Multiple controls stacked in same corner
 * <ControlGroup position="bottom-right" spacing="md">
 *   <ControlButton icon="ArrowsOut" onPress={handleReset} />
 *   <div className="flex flex-col">
 *     <ControlButton icon="Compass" onPress={handleCompass} />
 *     <ControlButton icon="NavigationArrow" onPress={handleGeolocate} />
 *   </div>
 * </ControlGroup>
 *
 * @example
 * // Horizontal group (e.g., for top toolbar)
 * <ControlGroup position="top-left" orientation="horizontal" spacing="sm">
 *   <ControlButton icon="Plus" onPress={zoomIn} />
 *   <ControlButton icon="Minus" onPress={zoomOut} />
 * </ControlGroup>
 */
export function ControlGroup({
  position = "top-right",
  className,
  style,
  children,
  orientation = "vertical",
  spacing = "md",
}: ControlGroupProps) {
  return (
    <ControlBase position={position} style={style}>
      <div
        className={twMerge(
          "flex",
          orientation === "vertical" ? "flex-col" : "flex-row",
          SPACING_CLASSES[spacing],
          className
        )}
      >
        {children}
      </div>
    </ControlBase>
  );
}
